import { useState, useEffect, useCallback, Fragment } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { fmtUSD, fmtNumber, isSuperadmin } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  BarChart3, Users, Building2, Layers, FileText, DollarSign,
  ChevronRight, ChevronDown, Loader2, Check, X, Eye, Coins, Cpu, RefreshCw, SlidersHorizontal, BrainCircuit, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USAGE_LIMIT = 50;

const USAGE_RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

function rangeFromISO(days) {
  if (days === 'all') return null;
  return new Date(Date.now() - Number(days) * 86400000).toISOString();
}

function StatCard({ icon: Icon, label, value, color = "text-[#0D1830]", bg = "bg-[#72A4BF]/10" }) {
  return (
    <Card className="border-[#E2E8ED] shadow-none">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center`}><Icon className={`w-5 h-5 ${color}`} /></div>
        <div><p className="text-xl font-bold text-[#0D1830]">{value}</p><p className="text-xs text-[#72A4BF]">{label}</p></div>
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [showTenantDetail, setShowTenantDetail] = useState(false);

  // ── Usage tab state ──────────────────────────────────────────────────────
  const [usageRuns, setUsageRuns] = useState([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usageOffset, setUsageOffset] = useState(0);
  const [usageSummary, setUsageSummary] = useState({ by_tenant: [], grand_total: { run_count: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0 } });
  const [usageTenant, setUsageTenant] = useState('all');
  const [usageDays, setUsageDays] = useState('30');
  const [usageLoading, setUsageLoading] = useState(false);
  const [expandedRuns, setExpandedRuns] = useState(() => new Set());
  // Pricing editor
  const [pricing, setPricing] = useState([]);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({});
  const [pricingSaving, setPricingSaving] = useState(false);
  // Nivela Brain (CAD↔token rate + margin % + infra % + USD→CAD FX)
  const [billingConfig, setBillingConfig] = useState(null);
  const [showNivelaBrain, setShowNivelaBrain] = useState(false);
  const [nivelaDraft, setNivelaDraft] = useState('');      // nivela_tokens_per_cad
  const [marginDraft, setMarginDraft] = useState('0');
  const [infraDraft, setInfraDraft] = useState('0');
  const [fxModeDraft, setFxModeDraft] = useState('auto');  // 'auto' | 'manual'
  const [fxManualDraft, setFxManualDraft] = useState('');
  const [nivelaSaving, setNivelaSaving] = useState(false);
  const [fxRefreshing, setFxRefreshing] = useState(false);
  // Subscription plans (read-only — the catalogue lives in Stripe)
  const [plans, setPlans] = useState([]);
  const [plansSyncing, setPlansSyncing] = useState(false);
  // The plan_id currently being auto-setup'd (disables that one row's button)
  const [bootstrappingPlanId, setBootstrappingPlanId] = useState(null);
  // Test-only simulation controls (in the tenant-detail dialog billing section).
  const [simDraft, setSimDraft] = useState({ tokens: '', advance_days: '30' });
  const [simBusy, setSimBusy] = useState(null);   // 'usage' | 'renewal' | null
  // Per-tenant billing (loaded inside the tenant-detail dialog)
  const [tenantBilling, setTenantBilling] = useState(null);
  const [grantDraft, setGrantDraft] = useState({ tokens: '', note: '' });
  const [grantSaving, setGrantSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, tenantsRes, feedbackRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/tenants`, { credentials: 'include' }),
        fetch(`${API}/admin/ai-feedback?status=pending`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (tenantsRes.ok) setTenants(await tenantsRes.json());
      if (feedbackRes.ok) setFeedback(await feedbackRes.json());
    } catch (e) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const usageQuery = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (usageTenant && usageTenant !== 'all') p.set('tenant_id', usageTenant);
    const from = rangeFromISO(usageDays);
    if (from) p.set('from', from);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [usageTenant, usageDays]);

  const fetchUsage = useCallback(async (reset = true) => {
    setUsageLoading(true);
    const offset = reset ? 0 : usageOffset + USAGE_LIMIT;
    try {
      const [runsRes, sumRes, bcRes] = await Promise.all([
        fetch(`${API}/admin/usage?${usageQuery({ limit: USAGE_LIMIT, offset })}`, { credentials: 'include' }),
        reset ? fetch(`${API}/admin/usage/summary?${usageQuery()}`, { credentials: 'include' }) : Promise.resolve(null),
        reset ? fetch(`${API}/admin/billing-config`, { credentials: 'include' }) : Promise.resolve(null),
      ]);
      if (runsRes.ok) {
        const data = await runsRes.json();
        setUsageRuns(prev => reset ? (data.runs || []) : [...prev, ...(data.runs || [])]);
        setUsageTotal(data.total || 0);
        setUsageOffset(offset);
      }
      if (sumRes && sumRes.ok) setUsageSummary(await sumRes.json());
      if (bcRes && bcRes.ok) setBillingConfig(await bcRes.json());
      if (reset) setExpandedRuns(new Set());
    } catch (e) {}
    finally { setUsageLoading(false); }
  }, [usageQuery, usageOffset]);

  // Re-load usage whenever filters change.
  useEffect(() => {
    fetchUsage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageTenant, usageDays]);

  const fetchPricing = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/model-pricing`, { credentials: 'include' });
      if (res.ok) setPricing(await res.json());
    } catch (e) {}
  }, []);

  const openPricing = async () => {
    await fetchPricing();
    setShowPricing(true);
  };
  // Seed the editable draft from the loaded pricing rows.
  useEffect(() => {
    if (showPricing) {
      const d = {};
      pricing.forEach(p => { d[p.model_id] = { input: String(p.input_usd_per_mtok ?? ''), output: String(p.output_usd_per_mtok ?? '') }; });
      setPricingDraft(d);
    }
  }, [showPricing, pricing]);

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      const entries = Object.entries(pricingDraft);
      for (const [model_id, v] of entries) {
        const orig = pricing.find(p => p.model_id === model_id);
        const inp = parseFloat(v.input), out = parseFloat(v.output);
        if (!Number.isFinite(inp) || !Number.isFinite(out)) continue;
        if (orig && Number(orig.input_usd_per_mtok) === inp && Number(orig.output_usd_per_mtok) === out) continue;
        await fetch(`${API}/admin/model-pricing/${encodeURIComponent(model_id)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ input_usd_per_mtok: inp, output_usd_per_mtok: out }),
        });
      }
      await fetchPricing();
      await fetchUsage(true);
      setShowPricing(false);
    } catch (e) {}
    finally { setPricingSaving(false); }
  };

  const openNivelaBrain = () => {
    setNivelaDraft(billingConfig?.nivela_tokens_per_cad != null ? String(billingConfig.nivela_tokens_per_cad) : '');
    setMarginDraft(billingConfig?.margin_pct != null ? String(billingConfig.margin_pct) : '0');
    setInfraDraft(billingConfig?.infra_pct != null ? String(billingConfig.infra_pct) : '0');
    setFxModeDraft(billingConfig?.usd_to_cad_mode === 'manual' ? 'manual' : 'auto');
    setFxManualDraft(
      billingConfig?.usd_to_cad_manual != null
        ? String(billingConfig.usd_to_cad_manual)
        : (billingConfig?.usd_to_cad != null ? String(billingConfig.usd_to_cad) : ''),
    );
    setShowNivelaBrain(true);
  };

  const saveNivelaBrain = async () => {
    const v = parseFloat(nivelaDraft);
    const m = parseFloat(marginDraft);
    const i = parseFloat(infraDraft);
    if (!Number.isFinite(v) || v <= 0) return;
    if (!Number.isFinite(m) || m < 0 || m >= 100) return;
    if (!Number.isFinite(i) || i < 0) return;
    const body = {
      nivela_tokens_per_cad: v, margin_pct: m, infra_pct: i,
      usd_to_cad_mode: fxModeDraft === 'manual' ? 'manual' : 'auto',
    };
    if (fxModeDraft === 'manual') {
      const fx = parseFloat(fxManualDraft);
      if (!Number.isFinite(fx) || fx <= 0) return;
      body.usd_to_cad_manual = fx;
    }
    setNivelaSaving(true);
    try {
      const res = await fetch(`${API}/admin/billing-config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) setBillingConfig(await res.json());
      await fetchUsage(true);
      setShowNivelaBrain(false);
    } catch (e) {}
    finally { setNivelaSaving(false); }
  };

  const refreshFx = async () => {
    setFxRefreshing(true);
    try {
      const res = await fetch(`${API}/admin/billing-config/refresh-fx`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        if (data.config) {
          setBillingConfig(data.config);
          if (fxModeDraft === 'auto' && data.config.usd_to_cad != null) {
            setFxManualDraft(String(data.config.usd_to_cad));
          }
        }
      }
    } catch (e) {}
    finally { setFxRefreshing(false); }
  };

  const fetchPlans = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/plans`, { credentials: 'include' });
      if (res.ok) setPlans(await res.json());
    } catch (e) {}
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const syncPlans = async () => {
    setPlansSyncing(true);
    try {
      const res = await fetch(`${API}/admin/plans/sync`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.plans) setPlans(data.plans);
        // Surface the read-only sync's findings: how many products are ready,
        // and the first few that still need setup in Stripe.
        const result = data.result || {};
        const ready = result.synced || 0;
        const needs = result.needs_setup || [];
        if (needs.length > 0) {
          const sample = needs.slice(0, 2).map(n => n.name || n.product_id).join(', ');
          const more = needs.length > 2 ? ` (+${needs.length - 2} more)` : '';
          toast.warning(
            `Synced ${ready} ready. ${needs.length} need setup in Stripe: ${sample}${more}`,
            { duration: 8000 },
          );
        } else if (ready > 0) {
          toast.success(`Synced ${ready} plan${ready === 1 ? '' : 's'} from Stripe.`);
        } else {
          toast.message('No active Products found in Stripe.');
        }
      } else {
        toast.error('Sync failed — check backend logs.');
      }
    } catch (e) {
      toast.error('Sync request failed.');
    }
    finally { setPlansSyncing(false); }
  };

  // Auto-setup: ask the backend to ensure the Meter + create the tiered
  // metered Price on this Product (using the metadata the superadmin set in
  // Stripe). On success the plan list refreshes and the row flips Active.
  const bootstrapPlanInStripe = async (planId, planName) => {
    setBootstrappingPlanId(planId);
    try {
      const res = await fetch(
        `${API}/admin/plans/${encodeURIComponent(planId)}/bootstrap-stripe`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.plans) setPlans(data.plans);
        toast.success(data.bootstrap?.message || `Auto-setup complete for ${planName}.`);
      } else {
        toast.error(data.detail || `Auto-setup failed for ${planName}.`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(`Auto-setup request failed for ${planName}.`);
    } finally {
      setBootstrappingPlanId(null);
    }
  };

  const loadTenantBilling = async (tenantId) => {
    try {
      const res = await fetch(`${API}/admin/tenants/${tenantId}/billing`, { credentials: 'include' });
      if (res.ok) setTenantBilling(await res.json());
    } catch (e) { setTenantBilling(null); }
  };

  const assignTenantPlan = async (tenantId, planId) => {
    try {
      await fetch(`${API}/admin/tenants/${tenantId}/billing`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ plan_id: planId }),
      });
      await loadTenantBilling(tenantId);
      await fetchUsage(true);
    } catch (e) {}
  };

  const grantTokens = async (tenantId) => {
    const v = parseFloat(grantDraft.tokens);
    if (!Number.isFinite(v) || v === 0) return;
    setGrantSaving(true);
    try {
      await fetch(`${API}/admin/tenants/${tenantId}/billing/grant`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ tokens: v, note: grantDraft.note || '', type: v >= 0 ? 'topup' : 'adjust' }),
      });
      setGrantDraft({ tokens: '', note: '' });
      await loadTenantBilling(tenantId);
      await fetchUsage(true);
    } catch (e) {}
    finally { setGrantSaving(false); }
  };

  // Pull canonical cycle dates from Stripe to repair drifted local ones.
  // Doesn't touch token counters — only fixes cycle_start / cycle_end.
  const refreshCycleFromStripe = async (tenantId) => {
    setSimBusy('refresh-cycle');
    try {
      const res = await fetch(`${API}/admin/tenants/${tenantId}/billing/refresh-cycle-from-stripe`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Cycle pulled from Stripe: ${data.cycle_start?.slice(0,10)} → ${data.cycle_end?.slice(0,10)}.`);
        await loadTenantBilling(tenantId);
      } else {
        toast.error(data.detail || 'Refresh cycle failed.');
      }
    } catch (e) { toast.error('Refresh cycle request failed.'); }
    finally { setSimBusy(null); }
  };

  // Test-only: simulate token consumption + cycle renewal so the user can
  // validate the overage → renewal → reset flow without running a takeoff.
  const simulateUsage = async (tenantId) => {
    const v = parseFloat(simDraft.tokens);
    if (!Number.isFinite(v) || v <= 0) {
      toast.error('Enter a positive number of tokens to simulate.');
      return;
    }
    setSimBusy('usage');
    try {
      const res = await fetch(`${API}/admin/tenants/${tenantId}/billing/simulate-usage`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ tokens: v }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const o = data.balance?.overage_tokens_this_cycle ?? 0;
        toast.success(`Simulated ${fmtNumber(v)} tokens. Overage this cycle: ${fmtNumber(o)}.`);
        await loadTenantBilling(tenantId);
      } else {
        toast.error(data.detail || 'Simulate usage failed.');
      }
    } catch (e) { toast.error('Simulate usage request failed.'); }
    finally { setSimBusy(null); }
  };

  const simulateRenewal = async (tenantId) => {
    const d = parseInt(simDraft.advance_days || '30', 10);
    if (!Number.isFinite(d) || d <= 0) {
      toast.error('advance_days must be > 0.');
      return;
    }
    setSimBusy('renewal');
    try {
      const res = await fetch(`${API}/admin/tenants/${tenantId}/billing/simulate-renewal`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ advance_days: d }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(`Renewal simulated. Cycle advanced ${d} days; base re-granted; overage reset to 0.`);
        await loadTenantBilling(tenantId);
      } else {
        toast.error(data.detail || 'Simulate renewal failed.');
      }
    } catch (e) { toast.error('Simulate renewal request failed.'); }
    finally { setSimBusy(null); }
  };

  const viewTenant = async (tenantId) => {
    try {
      const res = await fetch(`${API}/admin/tenants/${tenantId}`, { credentials: 'include' });
      if (res.ok) {
        setTenantDetail(await res.json());
        setShowTenantDetail(true);
        loadTenantBilling(tenantId);
      }
    } catch (e) {}
  };

  const reviewFeedback = async (feedbackId, status) => {
    try {
      await fetch(`${API}/admin/ai-feedback/${feedbackId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ status })
      });
      setFeedback(prev => prev.filter(f => f.feedback_id !== feedbackId));
    } catch (e) {}
  };

  const toggleRun = (runId) => {
    setExpandedRuns(prev => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
  };

  // Only the global superadmin sees this page. Tenant admins land here
  // only by typing the URL (the user-menu link is hidden for them); give a
  // clean message rather than a wall of 403'd fetches.
  if (user && !isSuperadmin(user)) {
    return (
      <div className="min-h-full bg-[#F7FAFB]" data-testid="admin-not-authorized">
        <main className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-[#E2E8ED] shadow-none">
            <CardHeader>
              <CardTitle className="text-lg text-[#0D1830]">Admin Panel — superadmin only</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-[#446E87] space-y-2">
              <p>This page is for the global Nivela administrator. Tenant-level admin features (your company profile, team, billing, and usage) are in <a href="/settings" className="text-[#1D4053] underline">Settings</a>.</p>
              <p className="text-xs text-[#72A4BF]">If you need access to platform-wide settings, contact your Nivela administrator.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-full bg-[#F7FAFB]">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>
    </div>
  );

  const USAGE_COLS = 10;
  const nivelaRate = billingConfig?.nivela_tokens_per_cad ?? usageSummary?.nivela_tokens_per_cad;

  return (
    <div className="min-h-full bg-[#F7FAFB]" data-testid="admin-page">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-[#0D1830] mb-6">Admin Panel</h1>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-[#F0F4F7] p-1 rounded-xl">
            <TabsTrigger value="overview" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="tenants" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">Tenants</TabsTrigger>
            <TabsTrigger value="usage" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">Usage</TabsTrigger>
            <TabsTrigger value="feedback" className="rounded-lg text-xs data-[state=active]:bg-[#0D1830] data-[state=active]:text-white">
              AI Feedback
              {feedback.length > 0 && <Badge className="ml-1 bg-red-500 text-white text-[8px] px-1 py-0 rounded-full border-0">{feedback.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" data-testid="admin-overview">
            {stats && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <StatCard icon={Building2} label="Tenants" value={stats.total_tenants} />
                  <StatCard icon={Users} label="Users" value={stats.total_users} color="text-blue-600" bg="bg-blue-50" />
                  <StatCard icon={Layers} label="Projects" value={stats.total_projects} color="text-emerald-600" bg="bg-emerald-50" />
                  <StatCard icon={FileText} label="Quotes" value={stats.total_quotes} color="text-purple-600" bg="bg-purple-50" />
                  <StatCard icon={BarChart3} label="Takeoffs" value={stats.total_takeoffs} color="text-[#0D1830]" bg="bg-[#72A4BF]/10" />
                </div>
                <Card className="border-[#E2E8ED] shadow-none">
                  <CardHeader><CardTitle className="text-sm">Projects by Status</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 flex-wrap">
                      {Object.entries(stats.projects_by_status || {}).filter(([,v]) => v > 0).map(([status, count]) => (
                        <div key={status} className="flex items-center gap-2 bg-[#F0F4F7] rounded-full px-3 py-1.5">
                          <span className="text-xs text-[#446E87] capitalize">{status}</span>
                          <Badge className="bg-[#0D1830] text-white border-0 text-[9px] px-1.5 py-0 rounded-full">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Tenants Tab */}
          <TabsContent value="tenants" data-testid="admin-tenants">
            <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                    <TableHead className="text-xs">Tenant</TableHead>
                    <TableHead className="text-xs text-right">Users</TableHead>
                    <TableHead className="text-xs text-right">Projects</TableHead>
                    <TableHead className="text-xs text-right">Quotes</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                    <TableHead className="text-xs w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => (
                    <TableRow key={t.tenant_id} className="border-[#F0F4F7]" data-testid={`tenant-row-${t.tenant_id}`}>
                      <TableCell className="text-xs">
                        <div>
                          <span className="font-medium text-[#0D1830]">{t.name || 'Unnamed'}</span>
                          <span className="text-[10px] text-[#72A4BF]/60 block">{t.tenant_id}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-right">{t.user_count}</TableCell>
                      <TableCell className="text-xs text-right">{t.project_count}</TableCell>
                      <TableCell className="text-xs text-right">{t.quote_count}</TableCell>
                      <TableCell className="text-xs text-[#72A4BF]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => viewTenant(t.tenant_id)} className="h-6 text-[10px] px-2" data-testid={`view-tenant-${t.tenant_id}`}>
                          <Eye className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {tenants.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-xs text-[#72A4BF]">No tenants yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Usage Tab — per-tenant / per-run / per-model LLM token usage & cost */}
          <TabsContent value="usage" data-testid="admin-usage">
            <div className="space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <StatCard icon={RefreshCw} label="Analysis runs" value={fmtNumber(usageSummary.grand_total?.run_count || 0)} />
                <StatCard icon={Cpu} label="Input tokens" value={fmtNumber(usageSummary.grand_total?.input_tokens || 0)} color="text-blue-600" bg="bg-blue-50" />
                <StatCard icon={Cpu} label="Output tokens" value={fmtNumber(usageSummary.grand_total?.output_tokens || 0)} color="text-purple-600" bg="bg-purple-50" />
                <StatCard icon={DollarSign} label="Total cost (USD)" value={fmtUSD(usageSummary.grand_total?.cost_usd || 0)} color="text-emerald-600" bg="bg-emerald-50" />
                <StatCard icon={BrainCircuit} label="Nivela Tokens" value={fmtNumber(usageSummary.grand_total?.nivela_tokens || 0)} color="text-[#1D4053]" bg="bg-[#72A4BF]/10" />
              </div>

              {/* Subscription plans — synced from Stripe (read-only) */}
              <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
                <CardHeader className="py-3 flex flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle className="text-xs text-[#72A4BF] uppercase tracking-wide">Subscription plans</CardTitle>
                    <p className="text-[10px] text-[#72A4BF]/70 mt-0.5">Catalogue lives in Stripe. Token allowance = monthly price × the Nivela Brain rate.</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={syncPlans} disabled={plansSyncing} className="h-7 text-[11px] px-2 gap-1" data-testid="sync-plans">
                    <RefreshCw className={`w-3 h-3 ${plansSyncing ? 'animate-spin' : ''}`} /> Sync from Stripe
                  </Button>
                </CardHeader>
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs text-right">Monthly (CAD)</TableHead>
                      <TableHead className="text-xs text-right">Base tokens / cycle</TableHead>
                      <TableHead className="text-xs text-right">Overage cap</TableHead>
                      <TableHead className="text-xs">Intervals</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plans
                      // Show only plans that are currently in Stripe's active
                      // catalogue (Active) or are there but still need setup
                      // (Needs setup). Old/archived Stripe products are hidden
                      // — they're either pruned by the sync (if unsubscribed)
                      // or kept inactive in the DB to resolve historic
                      // tenant_billing.plan_id lookups, but never shown here.
                      .filter(p => p.plan_id !== '__default__' && (p.active || p.needs_setup))
                      .map(p => (
                      <TableRow key={p.plan_id} className="border-[#F0F4F7]" data-testid={`plan-row-${p.plan_id}`}>
                        <TableCell className="text-xs">
                          <span className="font-medium text-[#0D1830]">{p.name}</span>
                          <span className="text-[10px] text-[#72A4BF]/60 block">{p.plan_id}</span>
                          {p.needs_setup && p.setup_reason && (
                            <span className="text-[10px] text-rose-700 mt-1 flex items-start gap-1 leading-snug" data-testid={`plan-setup-reason-${p.plan_id}`}>
                              <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
                              <span>{p.setup_reason}</span>
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-right">{fmtUSD(p.monthly_price_cad || 0)}</TableCell>
                        <TableCell className="text-xs text-right">{fmtNumber(p.base_tokens_per_month || 0)}</TableCell>
                        <TableCell className="text-xs text-right">{p.plan_overage_cap_tokens != null ? fmtNumber(p.plan_overage_cap_tokens) : 'Uncapped'}</TableCell>
                        <TableCell className="text-xs">
                          {/* Per-interval readiness pills: M = monthly, Y =
                              yearly. Tick = fully set up; warning = present
                              but needs setup; dash = not present on Product. */}
                          {(() => {
                            const ivs = p.intervals || {};
                            const monthly = ivs.monthly;
                            const yearly = ivs.yearly;
                            const pill = (label, state, tip) => (
                              <span title={tip} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                state === 'ok' ? 'bg-emerald-50 text-emerald-700'
                                : state === 'warn' ? 'bg-rose-50 text-rose-700'
                                : 'bg-gray-100 text-gray-500'
                              }`}>
                                {label} {state === 'ok' ? '✓' : state === 'warn' ? '⚠' : '–'}
                              </span>
                            );
                            const monthlyState = !monthly ? 'absent' : monthly.needs_setup ? 'warn' : 'ok';
                            const yearlyState = !yearly ? 'absent' : yearly.needs_setup ? 'warn' : 'ok';
                            return (
                              <div className="flex items-center gap-1.5">
                                {pill('M', monthlyState, monthly?.setup_reason || (monthly ? 'Monthly ready' : 'No monthly Price on Product'))}
                                {pill('Y', yearlyState, yearly?.setup_reason || (yearly ? 'Yearly ready' : 'No yearly Price on Product'))}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[9px] px-1.5 py-0 rounded-full border-0 ${
                              p.needs_setup ? 'bg-rose-100 text-rose-700'
                              : p.active ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-100 text-gray-500'
                            }`}>
                              {p.needs_setup ? 'Needs setup' : p.active ? 'Active' : 'Inactive'}
                            </Badge>
                            {p.needs_setup && (
                              <Button
                                size="sm" variant="outline"
                                onClick={() => bootstrapPlanInStripe(p.plan_id, p.name || p.plan_id)}
                                disabled={bootstrappingPlanId === p.plan_id}
                                className="h-6 text-[10px] px-2 gap-1 rounded-full"
                                data-testid={`plan-bootstrap-${p.plan_id}`}
                                title="Read this Product's metadata from Stripe and create the tiered metered Price on it (needed for billing). Idempotent — safe to re-click.">
                                {bootstrappingPlanId === p.plan_id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Cpu className="w-3 h-3" />}
                                Auto-setup in Stripe
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {plans.filter(p => p.plan_id !== '__default__' && (p.active || p.needs_setup)).length === 0 && (
                      <TableRow><TableCell colSpan={6} className="py-6 text-center text-xs text-[#72A4BF]">No plans synced. Create products in the Stripe Dashboard, then "Sync from Stripe".</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>

              {/* Filters */}
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={usageTenant} onValueChange={setUsageTenant}>
                  <SelectTrigger className="w-56 h-8 text-xs"><SelectValue placeholder="All tenants" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" className="text-xs">All tenants</SelectItem>
                    {tenants.map(t => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id} className="text-xs">{t.name || t.tenant_id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={usageDays} onValueChange={setUsageDays}>
                  <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {USAGE_RANGES.map(r => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => fetchUsage(true)} className="h-8 text-[11px] px-2 gap-1">
                  <RefreshCw className={`w-3 h-3 ${usageLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                {nivelaRate != null && (
                  <span className="text-[10px] text-[#72A4BF]">1 CAD = {fmtNumber(nivelaRate)} Nivela Tokens</span>
                )}
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={openNivelaBrain} className="h-8 text-[11px] px-2 gap-1" data-testid="nivela-brain">
                  <BrainCircuit className="w-3 h-3" /> Nivela Brain
                </Button>
                <Button variant="outline" size="sm" onClick={openPricing} className="h-8 text-[11px] px-2 gap-1" data-testid="edit-model-rates">
                  <SlidersHorizontal className="w-3 h-3" /> Edit model rates
                </Button>
              </div>

              {/* Per-tenant rollup (only meaningful when viewing all tenants) */}
              {usageTenant === 'all' && (usageSummary.by_tenant || []).length > 0 && (
                <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
                  <CardHeader className="py-3"><CardTitle className="text-xs text-[#72A4BF] uppercase tracking-wide">By tenant</CardTitle></CardHeader>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                        <TableHead className="text-xs">Tenant</TableHead>
                        <TableHead className="text-xs text-right">Runs</TableHead>
                        <TableHead className="text-xs text-right">Input tok</TableHead>
                        <TableHead className="text-xs text-right">Output tok</TableHead>
                        <TableHead className="text-xs text-right">Cost (USD)</TableHead>
                        <TableHead className="text-xs text-right">Nivela Tokens</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(usageSummary.by_tenant || []).map(b => (
                        <TableRow key={b.tenant_id} className="border-[#F0F4F7] cursor-pointer hover:bg-[#72A4BF]/5" onClick={() => setUsageTenant(b.tenant_id)}>
                          <TableCell className="text-xs">
                            <span className="font-medium text-[#0D1830]">{b.tenant_name || 'Unnamed'}</span>
                            <span className="text-[10px] text-[#72A4BF]/60 block">{b.tenant_id}</span>
                          </TableCell>
                          <TableCell className="text-xs text-right">{fmtNumber(b.run_count)}</TableCell>
                          <TableCell className="text-xs text-right">{fmtNumber(b.input_tokens)}</TableCell>
                          <TableCell className="text-xs text-right">{fmtNumber(b.output_tokens)}</TableCell>
                          <TableCell className="text-xs text-right">{fmtUSD(b.cost_usd)}</TableCell>
                          <TableCell className="text-xs text-right font-medium">{fmtNumber(b.nivela_tokens || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              )}

              {/* Runs table — one row per run, expandable to per-model breakdown */}
              <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                      <TableHead className="text-xs w-8"></TableHead>
                      <TableHead className="text-xs">Tenant</TableHead>
                      <TableHead className="text-xs">Project</TableHead>
                      <TableHead className="text-xs text-right w-12">Run #</TableHead>
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Model</TableHead>
                      <TableHead className="text-xs text-right">Input tok</TableHead>
                      <TableHead className="text-xs text-right">Output tok</TableHead>
                      <TableHead className="text-xs text-right">Cost (USD)</TableHead>
                      <TableHead className="text-xs text-right">Nivela Tokens</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usageRuns.map((r) => {
                      const open = expandedRuns.has(r.run_id);
                      return (
                        <Fragment key={r.run_id}>
                          <TableRow className="border-[#F0F4F7] cursor-pointer hover:bg-[#72A4BF]/5" onClick={() => toggleRun(r.run_id)} data-testid={`usage-run-${r.run_id}`}>
                            <TableCell>{open ? <ChevronDown className="w-3.5 h-3.5 text-[#72A4BF]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#72A4BF]" />}</TableCell>
                            <TableCell className="text-xs">
                              <span className="font-medium text-[#0D1830]">{r.tenant_name || 'Unnamed'}</span>
                              <span className="text-[10px] text-[#72A4BF]/60 block">{r.tenant_id || '—'}</span>
                            </TableCell>
                            <TableCell className="text-xs text-[#0D1830] max-w-[200px] truncate">{r.project_name || r.project_id}</TableCell>
                            <TableCell className="text-xs text-right">{r.run_number ?? '—'}</TableCell>
                            <TableCell className="text-xs text-[#72A4BF] whitespace-nowrap">{r.started_at ? new Date(r.started_at).toLocaleString() : '—'}</TableCell>
                            <TableCell className="text-xs">
                              <span className="text-[#446E87]">{r.selected_model || '—'}</span>
                              {r.analysis_mode && <Badge className="ml-1 bg-[#F0F4F7] text-[#446E87] border-0 text-[8px] px-1 py-0">{r.analysis_mode}</Badge>}
                              {r.totals?.cost_includes_unpriced_models && <Badge className="ml-1 bg-orange-100 text-orange-700 border-0 text-[8px] px-1 py-0">est.</Badge>}
                            </TableCell>
                            <TableCell className="text-xs text-right">{fmtNumber(r.totals?.input_tokens || 0)}</TableCell>
                            <TableCell className="text-xs text-right">{fmtNumber(r.totals?.output_tokens || 0)}</TableCell>
                            <TableCell className="text-xs text-right">{fmtUSD(r.totals?.cost_usd || 0)}</TableCell>
                            <TableCell className="text-xs text-right font-medium">{fmtNumber(r.nivela_tokens || 0)}</TableCell>
                          </TableRow>
                          {open && (
                            <TableRow className="border-[#F0F4F7] bg-[#FBFCFD]">
                              <TableCell colSpan={USAGE_COLS} className="py-2">
                                <div className="px-6">
                                  <p className="text-[10px] uppercase tracking-wide text-[#72A4BF] mb-1">Per-model breakdown · {r.call_count || 0} LLM call{(r.call_count || 0) === 1 ? '' : 's'} · {r.duration_s != null ? `${r.duration_s}s` : ''}</p>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-[#72A4BF]">
                                        <th className="text-left font-medium py-1">Model</th>
                                        <th className="text-right font-medium py-1">Calls</th>
                                        <th className="text-right font-medium py-1">Input tok</th>
                                        <th className="text-right font-medium py-1">Output tok</th>
                                        <th className="text-right font-medium py-1">Input $</th>
                                        <th className="text-right font-medium py-1">Output $</th>
                                        <th className="text-right font-medium py-1">Cost</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(r.by_model || []).map(m => (
                                        <tr key={m.model} className="border-t border-[#F0F4F7]">
                                          <td className="py-1 text-[#0D1830] flex items-center gap-1"><Coins className="w-3 h-3 text-[#72A4BF]" />{m.model}{!m.priced && <Badge className="ml-1 bg-orange-100 text-orange-700 border-0 text-[8px] px-1 py-0">no rate</Badge>}</td>
                                          <td className="py-1 text-right">{fmtNumber(m.calls)}</td>
                                          <td className="py-1 text-right">{fmtNumber(m.input_tokens)}</td>
                                          <td className="py-1 text-right">{fmtNumber(m.output_tokens)}</td>
                                          <td className="py-1 text-right text-[#72A4BF]">{m.priced ? fmtUSD(m.input_cost_usd, 4) : '—'}</td>
                                          <td className="py-1 text-right text-[#72A4BF]">{m.priced ? fmtUSD(m.output_cost_usd, 4) : '—'}</td>
                                          <td className="py-1 text-right font-medium">{m.priced ? fmtUSD(m.cost_usd, 4) : '—'}</td>
                                        </tr>
                                      ))}
                                      {(r.by_model || []).length === 0 && (
                                        <tr><td colSpan={7} className="py-2 text-center text-[#72A4BF]">No LLM calls recorded for this run</td></tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                    {usageRuns.length === 0 && (
                      <TableRow><TableCell colSpan={USAGE_COLS} className="py-10 text-center text-xs text-[#72A4BF]">
                        {usageLoading ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-[#1D4053]" /> : 'No analysis runs in this period'}
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>

              {usageRuns.length < usageTotal && (
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={() => fetchUsage(false)} disabled={usageLoading} className="h-8 text-[11px] px-3">
                    {usageLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Load more ({usageRuns.length} / {usageTotal})
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* AI Feedback Tab */}
          <TabsContent value="feedback" data-testid="admin-feedback">
            <Card className="border-[#E2E8ED] shadow-none overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8ED] bg-[#F7FAFB]">
                    <TableHead className="text-xs">Component</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Original</TableHead>
                    <TableHead className="text-xs">Corrected</TableHead>
                    <TableHead className="text-xs">Notes</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedback.map((fb) => (
                    <TableRow key={fb.feedback_id} className="border-[#F0F4F7]" data-testid={`feedback-row-${fb.feedback_id}`}>
                      <TableCell className="text-xs font-medium">{fb.component_name}</TableCell>
                      <TableCell>
                        <Badge className={`text-[9px] px-1.5 py-0 rounded-full border-0 ${
                          fb.feedback_type === 'correction' ? 'bg-blue-100 text-blue-700' :
                          fb.feedback_type === 'missing' ? 'bg-orange-100 text-orange-700' :
                          fb.feedback_type === 'incorrect' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{fb.feedback_type}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-[#72A4BF] max-w-[120px] truncate">{fb.original_value || '-'}</TableCell>
                      <TableCell className="text-xs text-[#0D1830] max-w-[120px] truncate">{fb.corrected_value || '-'}</TableCell>
                      <TableCell className="text-xs text-[#446E87] max-w-[150px] truncate">{fb.notes || '-'}</TableCell>
                      <TableCell className="text-xs text-[#72A4BF]">{fb.created_at ? new Date(fb.created_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => reviewFeedback(fb.feedback_id, 'approved')} className="h-6 w-6 p-0 text-emerald-600 hover:bg-emerald-50" data-testid={`approve-${fb.feedback_id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => reviewFeedback(fb.feedback_id, 'rejected')} className="h-6 w-6 p-0 text-red-500 hover:bg-red-50" data-testid={`reject-${fb.feedback_id}`}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {feedback.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="py-8 text-center text-xs text-[#72A4BF]">No pending feedback to review</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Tenant Detail Dialog */}
        <Dialog open={showTenantDetail} onOpenChange={setShowTenantDetail}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Tenant Details</DialogTitle>
              <DialogDescription>{tenantDetail?.tenant?.tenant_id}</DialogDescription>
            </DialogHeader>
            {tenantDetail && (
              <div className="space-y-4 py-2">
                <div>
                  <h4 className="text-xs font-semibold text-[#72A4BF] mb-2">USERS ({tenantDetail.users?.length || 0})</h4>
                  <div className="space-y-1.5">
                    {(tenantDetail.users || []).map(u => (
                      <div key={u.user_id} className="flex items-center justify-between bg-[#F7FAFB] rounded-lg px-3 py-2">
                        <div>
                          <span className="text-xs font-medium">{u.name || 'Unnamed'}</span>
                          <span className="text-[10px] text-[#72A4BF] block">{u.email}</span>
                        </div>
                        <Badge className="bg-[#F0F4F7] text-[#446E87] border-0 text-[9px] capitalize">{u.role || 'user'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[#72A4BF] mb-2">PROJECTS ({tenantDetail.projects?.length || 0})</h4>
                  <div className="space-y-1">
                    {(tenantDetail.projects || []).map(p => (
                      <div key={p.project_id} className="flex items-center justify-between bg-[#F7FAFB] rounded-lg px-3 py-1.5">
                        <span className="text-xs">{p.name}</span>
                        <Badge className="text-[9px] px-1.5 py-0 border-0 bg-gray-100 text-gray-600 capitalize">{p.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Billing — plan, balance, grant tokens */}
                <div className="border-t border-[#E2E8ED] pt-3">
                  <h4 className="text-xs font-semibold text-[#72A4BF] mb-2">BILLING</h4>
                  {tenantBilling ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-[#F7FAFB] rounded-lg p-2.5">
                          <p className="text-[9px] uppercase tracking-wide text-[#72A4BF]">Plan</p>
                          <p className="text-xs font-semibold text-[#0D1830]">{tenantBilling.plan?.name || '—'}</p>
                          <select value={tenantBilling.tenant_billing?.plan_id || ''}
                            onChange={(e) => assignTenantPlan(tenantDetail.tenant.tenant_id, e.target.value)}
                            className="mt-1 text-[10px] rounded-full border border-[#E2E8ED] px-2 py-0.5 bg-white w-full">
                            {plans.map(p => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div className="bg-[#F7FAFB] rounded-lg p-2.5">
                          <p className="text-[9px] uppercase tracking-wide text-[#72A4BF]">Effective balance</p>
                          <p className="text-xs font-semibold text-[#0D1830]">{fmtNumber(tenantBilling.tenant_billing?.effective_balance || 0)} Nivela Tokens</p>
                          <p className="text-[10px] text-[#72A4BF] mt-0.5">
                            Base {fmtNumber(tenantBilling.tenant_billing?.base_remaining_tokens || 0)} of {fmtNumber(tenantBilling.tenant_billing?.base_allowance_tokens || 0)} · Top-up {fmtNumber(tenantBilling.tenant_billing?.topup_balance_tokens || 0)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-[10px] text-[#72A4BF]">Cycle: {tenantBilling.tenant_billing?.cycle_start?.slice(0,10) || '—'} → {tenantBilling.tenant_billing?.cycle_end?.slice(0,10) || '—'} (base resets; top-up carries forward)</p>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => refreshCycleFromStripe(tenantDetail.tenant.tenant_id)}
                          disabled={simBusy !== null || !tenantBilling.tenant_billing?.stripe_subscription_id}
                          className="h-6 text-[10px] px-2 gap-1 text-[#446E87] hover:text-[#0D1830]"
                          title="Pull the canonical cycle dates from Stripe and overwrite local ones. Token counters untouched."
                          data-testid="refresh-cycle-btn">
                          {simBusy === 'refresh-cycle' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          Refresh cycle from Stripe
                        </Button>
                      </div>
                      <div className="flex items-end gap-2 pt-2 border-t border-[#E2E8ED]">
                        <div className="flex-1">
                          <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Grant top-up (Nivela Tokens)</label>
                          <Input type="number" step="1" className="h-7 text-xs mt-0.5" value={grantDraft.tokens}
                            onChange={(e) => setGrantDraft(d => ({ ...d, tokens: e.target.value }))}
                            placeholder="e.g. 1000 (or -100 to deduct)" />
                        </div>
                        <Input className="h-7 text-xs flex-1" placeholder="Note (optional)" value={grantDraft.note}
                          onChange={(e) => setGrantDraft(d => ({ ...d, note: e.target.value }))} />
                        <Button size="sm" onClick={() => grantTokens(tenantDetail.tenant.tenant_id)}
                          disabled={grantSaving || !grantDraft.tokens}
                          className="h-7 text-[11px] bg-[#0D1830] text-white rounded-full hover:opacity-90">
                          {grantSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Grant
                        </Button>
                      </div>

                      {/* Test-only simulation: drive overage + renewal without
                          running takeoffs. consume_for_run / apply_renewal run
                          for real — Stripe meter receives the events too. */}
                      <div className="pt-3 mt-2 border-t border-dashed border-rose-200 bg-rose-50/40 -mx-2 px-2 py-2 rounded">
                        <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wide mb-2">
                          Test renewal flow (admin / sandbox only)
                        </p>
                        <div className="flex items-end gap-2 flex-wrap">
                          <div className="flex-1 min-w-[140px]">
                            <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Simulate tokens</label>
                            <Input type="number" step="100" min="1" className="h-7 text-xs mt-0.5"
                              value={simDraft.tokens}
                              onChange={(e) => setSimDraft(d => ({ ...d, tokens: e.target.value }))}
                              placeholder="e.g. 5000"
                              data-testid="sim-tokens-input" />
                          </div>
                          <Button size="sm" onClick={() => simulateUsage(tenantDetail.tenant.tenant_id)}
                            disabled={simBusy !== null || !simDraft.tokens}
                            className="h-7 text-[11px] rounded-full"
                            variant="outline"
                            data-testid="sim-usage-btn">
                            {simBusy === 'usage' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                            Simulate usage
                          </Button>
                        </div>
                        <div className="flex items-end gap-2 flex-wrap mt-2">
                          <div className="w-[120px]">
                            <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Advance days</label>
                            <Input type="number" step="1" min="1" max="366" className="h-7 text-xs mt-0.5"
                              value={simDraft.advance_days}
                              onChange={(e) => setSimDraft(d => ({ ...d, advance_days: e.target.value }))}
                              data-testid="sim-days-input" />
                          </div>
                          <Button size="sm" onClick={() => simulateRenewal(tenantDetail.tenant.tenant_id)}
                            disabled={simBusy !== null}
                            className="h-7 text-[11px] rounded-full"
                            variant="outline"
                            data-testid="sim-renewal-btn">
                            {simBusy === 'renewal' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                            Simulate renewal
                          </Button>
                          <p className="text-[9px] text-[#72A4BF] flex-1 leading-snug">
                            Locally re-grants base & resets overage. Stripe-side invoice/charge needs a Test Clock.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[10px] text-[#72A4BF]">Loading billing…</p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter><Button onClick={() => setShowTenantDetail(false)} className="bg-[#0D1830] text-white rounded-full hover:opacity-90">Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Nivela Brain Dialog — CAD↔token rate, margin %, infra %, USD→CAD FX */}
        <Dialog open={showNivelaBrain} onOpenChange={setShowNivelaBrain}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><BrainCircuit className="w-4 h-4 text-[#1D4053]" /> Nivela Brain</DialogTitle>
              <DialogDescription>Per-run billing: LLM cost (USD) → CAD → +infra% → ÷(1−margin%) → Nivela Tokens. Tenants see only tokens.</DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Token price</label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-[#446E87]">1 CAD =</span>
                  <Input type="number" step="1" min="1" className="h-8 w-32 text-xs"
                    value={nivelaDraft} onChange={(e) => setNivelaDraft(e.target.value)} data-testid="nivela-rate-input" />
                  <span className="text-xs text-[#446E87]">Nivela Tokens</span>
                </div>
                {Number.isFinite(parseFloat(nivelaDraft)) && parseFloat(nivelaDraft) > 0 && (
                  <p className="text-[10px] text-[#72A4BF] mt-1">≈ 1 Nivela Token = ${(1 / parseFloat(nivelaDraft)).toFixed(6)} CAD</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Infra %</label>
                  <Input type="number" step="1" min="0" className="h-8 text-xs mt-1"
                    value={infraDraft} onChange={(e) => setInfraDraft(e.target.value)} data-testid="nivela-infra-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">Margin %</label>
                  <Input type="number" step="1" min="0" max="99" className="h-8 text-xs mt-1"
                    value={marginDraft} onChange={(e) => setMarginDraft(e.target.value)} data-testid="nivela-margin-input" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-[#72A4BF]">USD → CAD exchange rate</label>
                <div className="flex items-center gap-2 mt-1">
                  <select value={fxModeDraft} onChange={(e) => setFxModeDraft(e.target.value)}
                    className="h-8 text-xs rounded-lg border border-[#E2E8ED] px-2 bg-white" data-testid="nivela-fx-mode">
                    <option value="auto">Auto (live)</option>
                    <option value="manual">Manual</option>
                  </select>
                  {fxModeDraft === 'manual' ? (
                    <Input type="number" step="0.0001" min="0" className="h-8 w-28 text-xs"
                      value={fxManualDraft} onChange={(e) => setFxManualDraft(e.target.value)}
                      placeholder="e.g. 1.37" data-testid="nivela-fx-manual" />
                  ) : (
                    <span className="text-xs text-[#0D1830]">1 USD = {billingConfig?.usd_to_cad != null ? Number(billingConfig.usd_to_cad).toFixed(4) : '—'} CAD</span>
                  )}
                  {fxModeDraft === 'auto' && (
                    <Button variant="ghost" size="sm" onClick={refreshFx} disabled={fxRefreshing} className="h-7 text-[10px] px-2 gap-1">
                      <RefreshCw className={`w-3 h-3 ${fxRefreshing ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                  )}
                </div>
                {billingConfig?.usd_to_cad_updated_at && (
                  <p className="text-[10px] text-[#72A4BF]/70 mt-1">
                    FX updated {new Date(billingConfig.usd_to_cad_updated_at).toLocaleString()}
                    {billingConfig.usd_to_cad_source ? ` · ${billingConfig.usd_to_cad_source}` : ''}
                  </p>
                )}
              </div>
              {(() => {
                const r = parseFloat(nivelaDraft), m = parseFloat(marginDraft), i = parseFloat(infraDraft);
                const fx = fxModeDraft === 'manual' ? parseFloat(fxManualDraft) : Number(billingConfig?.usd_to_cad);
                if (!(r > 0) || !(m >= 0) || m >= 100 || !(i >= 0) || !(fx > 0)) return null;
                const cad = (0.10 * fx * (1 + i / 100)) / (1 - m / 100);
                return (
                  <p className="text-[10px] text-[#72A4BF] bg-[#F7FAFB] rounded-lg p-2">
                    Example: a <span className="text-[#0D1830]">$0.10 USD</span> run →
                    <span className="text-[#0D1830]"> ${cad.toFixed(4)} CAD</span> (×{fx} FX, +{i}% infra, ÷{(1 - m / 100).toFixed(2)} for {m}% margin) →
                    <span className="text-[#0D1830] font-semibold"> {fmtNumber(cad * r)} Nivela Tokens</span>.
                  </p>
                );
              })()}
              {billingConfig?.updated_at && (
                <p className="text-[10px] text-[#72A4BF]/70">Last changed {new Date(billingConfig.updated_at).toLocaleString()}{billingConfig.updated_by ? ` by ${billingConfig.updated_by}` : ''}</p>
              )}
              <p className="text-[10px] text-[#72A4BF]/70">Past runs aren't re-billed — each run snapshots the rate, FX, infra and margin in effect at run time.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNivelaBrain(false)} className="text-xs">Cancel</Button>
              <Button onClick={saveNivelaBrain} disabled={nivelaSaving || !(parseFloat(nivelaDraft) > 0) || !(parseFloat(marginDraft) >= 0) || parseFloat(marginDraft) >= 100 || !(parseFloat(infraDraft) >= 0) || (fxModeDraft === 'manual' && !(parseFloat(fxManualDraft) > 0))} className="bg-[#0D1830] text-white rounded-full hover:opacity-90 text-xs">
                {nivelaSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Model Pricing Dialog — edit $/1M-token rates used to compute all costs */}
        <Dialog open={showPricing} onOpenChange={setShowPricing}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Model rates ($ per 1M tokens)</DialogTitle>
              <DialogDescription>Costs on the Usage tab are recomputed from these rates.</DialogDescription>
            </DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-[#72A4BF] text-left">
                    <th className="py-1 font-medium">Model</th>
                    <th className="py-1 font-medium w-28 text-right">Input $/MTok</th>
                    <th className="py-1 font-medium w-28 text-right">Output $/MTok</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map(p => (
                    <tr key={p.model_id} className="border-t border-[#F0F4F7]">
                      <td className="py-1.5 text-[#0D1830]">{p.model_id}</td>
                      <td className="py-1.5">
                        <Input type="number" step="0.01" min="0" className="h-7 text-xs text-right"
                          value={pricingDraft[p.model_id]?.input ?? ''}
                          onChange={(e) => setPricingDraft(d => ({ ...d, [p.model_id]: { ...d[p.model_id], input: e.target.value } }))} />
                      </td>
                      <td className="py-1.5">
                        <Input type="number" step="0.01" min="0" className="h-7 text-xs text-right"
                          value={pricingDraft[p.model_id]?.output ?? ''}
                          onChange={(e) => setPricingDraft(d => ({ ...d, [p.model_id]: { ...d[p.model_id], output: e.target.value } }))} />
                      </td>
                    </tr>
                  ))}
                  {pricing.length === 0 && (
                    <tr><td colSpan={3} className="py-4 text-center text-[#72A4BF]">No rates loaded</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowPricing(false)} className="text-xs">Cancel</Button>
              <Button onClick={savePricing} disabled={pricingSaving} className="bg-[#0D1830] text-white rounded-full hover:opacity-90 text-xs">
                {pricingSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save rates
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
